import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { InboxSectionProps } from '@/infra/plugin-registry';
import { Project, User } from '@/infra/types/domain';
import { fetchInboxOutlines } from '../ducks';
import { updateTodoRequested } from '@/features/todos/ducks';
import { selectHouseholdUsers } from '@/features/household/selectors';
import {
  classifyPlanned,
  classifyRowStates,
  selectInboxOutlineProjects,
  selectInboxOutlinesLoading,
  selectInboxOutlineTodos,
} from '../selectors';
import { flattenProjectTree } from '../treeFlatten';
import { InboxProjectRow } from './InboxProjectRow';
import { ProjectEditModal } from './ProjectEditModal';

// Collapse state spans two levels: a per-view set of collapsed project GROUP
// headers, and per-project sets of collapsed parent ROWS. Both persist under
// one localStorage key so the inbox hierarchy survives a refresh, mirroring
// Outline's loadCollapsed/saveCollapsed scheme.
const COLLAPSE_STORAGE_KEY = 'projects.inbox.collapsed';

interface CollapseStore {
  // Collapsed project-group header ids.
  groups: string[];
  // Collapsed parent-row ids, keyed per project.
  rows: Record<string, string[]>;
}

function loadCollapse(): CollapseStore {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (!raw) return { groups: [], rows: {} };
    const parsed = JSON.parse(raw);
    return {
      groups: Array.isArray(parsed?.groups) ? parsed.groups : [],
      rows: parsed?.rows && typeof parsed.rows === 'object' ? parsed.rows : {},
    };
  } catch {
    return { groups: [], rows: {} };
  }
}

function saveCollapse(store: CollapseStore): void {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore quota errors
  }
}

export const InboxProjectsView: React.FC<InboxSectionProps> = () => {
  const dispatch = useDispatch();
  const projects = useSelector(selectInboxOutlineProjects);
  const loading = useSelector(selectInboxOutlinesLoading);
  // Default to [] so a store without the household slice (and the >1-member
  // avatar gate) is safe.
  const users = useSelector(selectHouseholdUsers) ?? [];
  const [searchParams, setSearchParams] = useSearchParams();
  const editProjectId = searchParams.get('projectEdit');

  const [collapse, setCollapse] = useState<CollapseStore>(() => loadCollapse());
  const collapsedGroups = useMemo(() => new Set(collapse.groups), [collapse.groups]);

  useEffect(() => {
    dispatch(fetchInboxOutlines());
  }, [dispatch]);

  const toggleGroup = useCallback((projectId: string) => {
    setCollapse((prev) => {
      const groups = prev.groups.includes(projectId)
        ? prev.groups.filter((id) => id !== projectId)
        : [...prev.groups, projectId];
      const next = { ...prev, groups };
      saveCollapse(next);
      return next;
    });
  }, []);

  const toggleRow = useCallback((projectId: string, todoId: string) => {
    setCollapse((prev) => {
      const current = prev.rows[projectId] ?? [];
      const updated = current.includes(todoId)
        ? current.filter((id) => id !== todoId)
        : [...current, todoId];
      const next = { ...prev, rows: { ...prev.rows, [projectId]: updated } };
      saveCollapse(next);
      return next;
    });
  }, []);

  const openEdit = useCallback(
    (projectId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('projectEdit', projectId);
        return next;
      });
    },
    [setSearchParams],
  );

  // Completion dispatch for leaf-row checkboxes. Mirrors TodosPage.handleUpdate:
  // a PATCH via updateTodoRequested. The schedule-complete itself is dispatched
  // inside useScheduleCompletion; this handles the uncomplete path. The cross-
  // slice replaceTodo patch keeps the inline hierarchy's status in sync.
  const handleUpdate = useCallback(
    (update: { id: string; [key: string]: unknown }) => {
      const { id, ...changes } = update;
      dispatch(updateTodoRequested(id, changes));
    },
    [dispatch],
  );

  const closeEdit = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('projectEdit');
      return next;
    });
    // Refetch so the inline hierarchy reflects edits made in the modal (§R6:
    // the inline view may be briefly stale behind the open modal).
    dispatch(fetchInboxOutlines());
  }, [setSearchParams, dispatch]);

  if (projects.length === 0) {
    return (
      <div className="inbox-projects">
        <div className="inbox-projects__empty">
          {loading ? 'Loading projects…' : 'No active projects.'}
        </div>
        {editProjectId && (
          <ProjectEditModal projectId={editProjectId} onClose={closeEdit} />
        )}
      </div>
    );
  }

  return (
    <div className="inbox-projects">
      {projects.map((project) => (
        <ProjectGroup
          key={project.id}
          project={project}
          collapsedGroup={collapsedGroups.has(project.id)}
          collapsedRows={collapse.rows[project.id] ?? []}
          onToggleGroup={toggleGroup}
          onToggleRow={toggleRow}
          onEdit={openEdit}
          onUpdate={handleUpdate}
          users={users}
        />
      ))}
      {editProjectId && (
        <ProjectEditModal projectId={editProjectId} onClose={closeEdit} />
      )}
    </div>
  );
};

interface ProjectGroupProps {
  project: Project;
  collapsedGroup: boolean;
  collapsedRows: string[];
  onToggleGroup: (projectId: string) => void;
  onToggleRow: (projectId: string, todoId: string) => void;
  onEdit: (projectId: string) => void;
  onUpdate: (update: { id: string; [key: string]: unknown }) => void;
  users: User[];
}

// One collapsible group per active project. Classification + flatten run once
// per group (over that project's flat list) rather than per row.
function ProjectGroup({
  project,
  collapsedGroup,
  collapsedRows,
  onToggleGroup,
  onToggleRow,
  onEdit,
  onUpdate,
  users,
}: ProjectGroupProps) {
  const todosSelector = useMemo(
    () => selectInboxOutlineTodos(project.id),
    [project.id],
  );
  const todos = useSelector(todosSelector);

  const childrenMap = useMemo(() => {
    const map: Record<string, typeof todos> = {};
    for (const t of todos) {
      const key = t.parent_todo_id ?? '__root__';
      (map[key] ||= []).push(t);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => {
        const sa = a.sort_order ?? Number.POSITIVE_INFINITY;
        const sb = b.sort_order ?? Number.POSITIVE_INFINITY;
        if (sa !== sb) return sa - sb;
        return (a.created_at || '').localeCompare(b.created_at || '');
      });
    }
    return map;
  }, [todos]);

  const rowStates = useMemo(() => classifyRowStates(todos), [todos]);
  const planned = useMemo(() => classifyPlanned(todos), [todos]);

  const collapsedSet = useMemo(() => new Set(collapsedRows), [collapsedRows]);
  const rows = useMemo(
    () => flattenProjectTree(childrenMap['__root__'] ?? [], childrenMap, collapsedSet),
    [childrenMap, collapsedSet],
  );

  return (
    <div className="inbox-projects__group">
      <div className="inbox-projects__group-header">
        <button
          type="button"
          className="inbox-projects__group-chevron"
          aria-label={collapsedGroup ? 'Expand project' : 'Collapse project'}
          aria-expanded={!collapsedGroup}
          onClick={() => onToggleGroup(project.id)}
        >
          {collapsedGroup ? '▸' : '▾'}
        </button>
        <span className="inbox-projects__group-title">{project.title}</span>
        <button
          type="button"
          className="inbox-projects__group-edit"
          onClick={() => onEdit(project.id)}
        >
          Edit
        </button>
      </div>
      {!collapsedGroup && (
        <div className="inbox-projects__group-body" role="list">
          {rows.length === 0 ? (
            <div className="inbox-projects__group-empty">No tasks.</div>
          ) : (
            rows.map((row) => (
              <InboxProjectRow
                key={row.todo.id}
                todo={row.todo}
                depth={row.depth}
                connectors={row.connectors}
                hasChildren={row.hasChildren}
                collapsed={row.collapsed}
                onToggleCollapsed={(id) => onToggleRow(project.id, id)}
                onUpdate={onUpdate}
                users={users}
                rowState={rowStates[row.todo.id] ?? 'ready'}
                planned={planned.has(row.todo.id)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default InboxProjectsView;
